import { api, ApiError, setSignedOutHandler } from "./api";
import { S, actions, isAdmin, isStaff, setRenderer, render, on } from "./state";
import { showPaomThanks, showSeasonCard } from "./seasoncard";
import { bellHtml, detectPush } from "./push";
import { $, I, MONO, PINS, closeSheet, meAvatar, toast, wirePhotos } from "./ui";
import { schoolYearLabel } from "../shared/constants";
import type { MeResponse } from "../shared/types";
import { signinView } from "./views/signin";
import { completionView } from "./views/completion";
import { homeView } from "./views/home";
import { profileView } from "./views/profile";
import { adminView, resetAdminCache } from "./views/admin";
import { calendar, openEventById, resetPeopleCache } from "./views/calendar";
import { resetManage } from "./views/manage";
import { resetEvents } from "./events";
import { checkinView, resetCheckin } from "./views/checkin";
import { filesView, resetFiles } from "./views/files";
import "./views/feedback";
import { birthdayConfetti, showLoginMessage } from "./views/sitetools";

const ROUTES = ["home", "calendar", "checkin", "files", "profile", "admin"];

function applyTheme() {
  const t = S.me?.user.theme || "system";
  if (t === "system") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", t);
}

function viewAsBar(me: MeResponse) {
  if (!me.canViewAs) return "";
  const cur = me.viewAs || "admin";
  const opts: [string, string][] = [
    ["admin", "Admin (You)"],
    ["admissions", "Admissions Staff"],
    ["pa", "Parent Ambassador"],
    ["sa", "Student Ambassador"],
  ];
  return `<div class="proto" role="region" aria-label="Admin tools"><b>Testing</b>
    <label><span class="vlabel">View As</span><select data-change="view-as" aria-label="View As">${opts
      .map(([v, l]) => `<option value="${v}" ${v === cur ? "selected" : ""}>${l}</option>`)
      .join("")}</select></label><span class="sp"></span>
    ${me.launched ? "" : '<span class="small">Pre-launch: emails only go to you</span>'}</div>`;
}

function shell(inner: string) {
  const me = S.me!;
  const links: [string, string, string][] = [["home", "Home", I.home], ["calendar", "Calendar", I.cal]];
  if (isStaff()) links.push(["checkin", "Check-in", I.checkin]);
  links.push(["files", "Files", I.file], ["profile", "Profile", I.user]);
  if (isStaff()) links.push(["admin", isAdmin() ? "Admin" : "Manage", I.admin]);
  const nav = () => links.map(([r, l, ic]) => `<a href="#${r}" class="${S.route === r ? "on" : ""}">${ic}<span>${l}</span></a>`).join("");
  return `${viewAsBar(me)}
  <header class="top">
    <a class="brand" href="#home">${MONO(46)}<span>Ambassador Scheduling<small>${schoolYearLabel(me.schoolYear)}</small></span></a>
    <span class="sp"></span>${bellHtml()}${meAvatar(me.user, S.photoBust)}
  </header>
  <div class="maint ${me.maintenance ? "on" : ""}">⚠️ Maintenance Mode - We'll be back soon.</div>
  <div class="layout">
    <nav class="side" aria-label="Main">${nav()}</nav>
    <main id="main">${inner}<footer class="foot"><button data-act="open-feedback">Send Feedback</button><span class="tag" aria-hidden="true">•</span><span class="tag">Built with Spartan Pride 💙</span></footer></main>
  </div>
  <nav class="tabs" aria-label="Main">${nav()}</nav>`;
}

async function draw() {
  const app = $("#app")!;
  if (!S.me) {
    app.innerHTML = signinView();
    afterSignin();
    return;
  }
  applyTheme();
  PINS.map = S.me.pins || {};
  if (!S.me.user.profileComplete) {
    app.innerHTML = completionView();
    wirePhotos(app);
    return;
  }
  const r = ROUTES.includes(S.route) && (!["admin", "checkin"].includes(S.route) || isStaff()) ? S.route : "home";
  if (r !== S.route) history.replaceState(null, "", "#" + r);
  S.route = r;
  const inner = r === "profile" ? profileView() : r === "admin" ? adminView() : r === "calendar" ? calendar() : r === "checkin" ? checkinView() : r === "files" ? filesView() : homeView();
  app.innerHTML = shell(inner);
  wirePhotos(app);
  // A season card shows once, on top of whatever page they opened.
  if (S.me.card && !cardShown) {
    cardShown = true;
    showSeasonCard(S.me.card);
  } else if (S.me.paomThanks && !paomShown) {
    // The PA of the Month thank-you waits its turn behind a season card (it shows on the next visit).
    paomShown = true;
    showPaomThanks(S.me.paomThanks, S.me.user.first, true);
  }
  // A waiting login message, once per page load (cards come first; it shows on the next visit).
  else if (S.me.loginMessage && !msgShown) {
    msgShown = true;
    showLoginMessage(S.me.loginMessage);
  }
  // Birthday confetti on the first visit of their birthday.
  if (S.me.birthdayConfetti && !bdayShown) {
    bdayShown = true;
    setTimeout(birthdayConfetti, 400);
    void api("/api/me/birthday-seen", { body: {} }).catch(() => {});
  }
}
let cardShown = false;
let paomShown = false;
let msgShown = false;
let bdayShown = false;

function afterSignin() {
  const first = document.querySelector<HTMLInputElement>(S.signin.step === "email" ? "#em" : "#code input");
  first?.focus();
}

setRenderer(() => void draw());

function route() {
  const h = location.hash.replace(/^#/, "") || "home";
  S.route = h.split(/[/?]/)[0];
  // Notification links: #calendar?event=123 opens that event's pop-up.
  const ev = Number(new URLSearchParams(h.split("?")[1] || "").get("event"));
  if (ev && S.me) void openEventById(ev);
  // Check-ins change through the day, so fetch them fresh on each visit.
  resetCheckin();
  resetFiles();
  closeSheet();
  render();
  window.scrollTo(0, 0);
}

export async function loadMe() {
  try {
    S.me = await api<MeResponse>("/api/me");
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) throw e;
    S.me = null;
  }
}

setSignedOutHandler(() => {
  S.me = null;
  cardShown = false;
  S.signin = { step: "email", email: "" };
  closeSheet();
  render();
});

on({
  "close-sheet": () => closeSheet(),
  "view-as": async (el) => {
    try {
      S.me = await api<MeResponse>("/api/me/view-as", { body: { role: (el as HTMLSelectElement).value } });
      resetAdminCache();
      // Different roles see different events and details, so start fresh.
      resetEvents();
      resetPeopleCache();
      resetManage();
      resetCheckin();
      toast(S.me.viewAs ? `Viewing as ${(el as HTMLSelectElement).selectedOptions[0].text}` : "Back to your own view");
      render();
    } catch (e) {
      toast((e as Error).message);
    }
  },
});

function dispatch(kind: "act" | "change" | "input", ev: Event) {
  const el = (ev.target as HTMLElement).closest<HTMLElement>(`[data-${kind}]`);
  if (!el) return;
  const name = el.dataset[kind]!;
  const h = actions[name];
  if (!h) return;
  if (kind === "act") ev.preventDefault();
  void h(el, ev);
}
document.addEventListener("click", (e) => dispatch("act", e));
document.addEventListener("change", (e) => dispatch("change", e));
document.addEventListener("input", (e) => dispatch("input", e));
document.addEventListener("submit", (e) => {
  const f = (e.target as HTMLElement).closest<HTMLFormElement>("form[data-submit]");
  if (!f) return;
  e.preventDefault();
  void actions[f.dataset.submit!]?.(f, e);
});
$("#scrim")?.addEventListener("click", closeSheet);
window.addEventListener("hashchange", route);
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeSheet();
  // Small icon controls inside other buttons (like a day's +) are spans with role="button".
  const t = e.target as HTMLElement;
  if ((e.key === "Enter" || e.key === " ") && t.getAttribute?.("role") === "button" && t.dataset.act) {
    e.preventDefault();
    t.click();
  }
});

(async () => {
  try {
    await loadMe();
  } catch {
    $("#app")!.innerHTML = `<div class="boot">Couldn't reach the server. Please reload.</div>`;
    return;
  }
  route();
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
    // A tapped notification asks an open window to go to its link.
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data?.type === "open") location.href = e.data.url;
    });
  }
  if (S.me) void detectPush();
})();
